// Contextual messaging types -- iteration 031, updated 042, 057
import type { DocumentAttachment } from "./document.js";

export const MESSAGE_CONTEXT_TYPES = ["team", "player", "event", "match", "age_group", "school", "sport", "direct"] as const;
export type MessageContextType = (typeof MESSAGE_CONTEXT_TYPES)[number];

export const RECIPIENT_GROUPS = ["parents", "coaches", "players"] as const;
export type RecipientGroup = (typeof RECIPIENT_GROUPS)[number];

// 163 -- message category (split event-info from team messages) + sub-group
// targeting (forwards/backs, resolved from team positions at send time).
export const MESSAGE_CATEGORIES = ["event_info", "team", "general"] as const;
export type MessageCategory = (typeof MESSAGE_CATEGORIES)[number];

export const MESSAGE_SUBGROUPS = ["forwards", "backs"] as const;
export type MessageSubgroup = (typeof MESSAGE_SUBGROUPS)[number];

/** Jersey-number ranges that define the sub-groups (rugby-oriented). */
export const SUBGROUP_JERSEY_RANGE: Record<MessageSubgroup, { min: number; max: number }> = {
  forwards: { min: 1, max: 8 },
  backs: { min: 9, max: 15 },
};

// --- Structured Response Options (057) ---

export interface ResponseOption {
  label: string;   // Display label (e.g., "Yes", "Attending")
  value: string;   // Machine value (e.g., "yes", "attending") -- lowercase snake_case
  color?: string;  // Optional UI hint ("green", "red", "yellow")
}

export const RESPONSE_PRESETS = {
  yes_no: [
    { label: "Yes", value: "yes", color: "green" },
    { label: "No", value: "no", color: "red" },
  ],
  attending: [
    { label: "Attending", value: "attending", color: "green" },
    { label: "Not attending", value: "not_attending", color: "red" },
    { label: "Maybe", value: "maybe", color: "yellow" },
  ],
  arrival: [
    { label: "On our way", value: "on_our_way", color: "yellow" },
    { label: "Arrived", value: "arrived", color: "green" },
  ],
} as const;

export type ResponsePresetKey = keyof typeof RESPONSE_PRESETS;

export interface MessageResponsePublic {
  id: string;
  message_id: string;
  recipient_id: string;
  user_id: string;
  user_name: string;
  value: string;
  created_at: string;
}

export interface MessageResponseAggregation {
  total_recipients: number;
  responded_count: number;
  counts: Record<string, number>; // value -> count
  recipients: Array<{
    user_id: string;
    user_name: string;
    group: string;
    latest_response: string | null;
    responded_at: string | null;
    delivered_at: string | null;
    read_at: string | null;
  }>;
}

export interface MessageDeliveryStatus {
  total: number;
  delivered: number;
  read: number;
  responded: number;
  recipients: Array<{
    user_id: string;
    user_name: string;
    group: string;
    delivered_at: string | null;
    read_at: string | null;
    latest_response: string | null;
    responded_at: string | null;
  }>;
}

// --- Message types ---

export interface MessagePublic {
  id: string;
  sender_id: string;
  sender_name: string;
  school_id: string;
  context_type: MessageContextType;
  context_id: string;
  context_label: string;
  groups: RecipientGroup[];
  category: MessageCategory; // 163
  subgroup: MessageSubgroup | null; // 163
  content: string;
  response_options: ResponseOption[] | null;
  sent_at: string;
  recipient_count: number;
  recipients_by_group: Record<string, number>;
  response_summary?: Record<string, number>; // value -> count
  document?: DocumentAttachment;
  _actions: MessageAction[];
}

export type MessageAction = "view" | "respond";

export interface MessagePreview {
  total: number;
  parents: number;
  coaches: number;
  players: number;
  direct?: number;
  school_id?: string;
}

// Recipient picker (for direct messaging)
export interface RecipientPickerUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  roles: string[];
}

export interface RecipientPickerResponse {
  recipients: RecipientPickerUser[];
}

// Recipient view (inbox)
export interface ReceivedMessagePublic {
  id: string;
  sender_name: string;
  context_type: MessageContextType;
  context_label: string;
  category: MessageCategory; // 163 -- event-info vs team stream
  subgroup: MessageSubgroup | null; // 163
  content: string;
  response_options: ResponseOption[] | null;
  sent_at: string;
  my_latest_response: string | null;
  document?: DocumentAttachment;
}

export interface ReceivedMessageListResponse {
  messages: ReceivedMessagePublic[];
}

// Response types
export interface SendMessageResponse {
  message: MessagePublic;
}

export interface MessagePreviewResponse {
  preview: MessagePreview;
}

export interface MessageListResponse {
  messages: MessagePublic[];
}

export interface RespondToMessageResponse {
  response: {
    message_id: string;
    user_id: string;
    value: string;
    created_at: string;
  };
}

export interface MessageResponsesResponse {
  aggregation: MessageResponseAggregation;
}

export interface MessageRecipientsResponse {
  delivery: MessageDeliveryStatus;
}
